import { Link } from 'react-router-dom';
import { serviceTableData } from '../data/contract';
import { useInView } from '../hooks/useInView';
import { Inquiry } from '../features/Inquiry/Inquiry';
import styles from './ContractPage.module.css';
import { Seo } from '../components/Seo';
import { breadcrumbJsonLd } from '../lib/structuredData';

const FEATURES = [
  {
    n: '01',
    title: '즉시 개시',
    desc: '계약 체결 후 24시간 이내 전담 변호사가 배정됩니다. 분쟁이 발생한 그 순간부터 지원이 시작됩니다.',
  },
  {
    n: '02',
    title: '사건별 맞춤 대응',
    desc: '진료비 분쟁·악성 리뷰·환자 민원·보건소 조사 등 모든 유형의 의료법률 문제에 대응합니다.',
  },
  {
    n: '03',
    title: '투명한 비용 구조',
    desc: '사전 견적 확인 후 진행합니다. 숨은 비용 없이 합의된 금액만 청구됩니다.',
  },
  {
    n: '04',
    title: '의사 출신 변호사 직접 담당',
    desc: '의사·치과의사 면허를 보유한 변호사가 임상 기준으로 직접 사건을 검토하고 대응합니다.',
  },
];

const INCLUDES = [
  { label: '긴급 법률 상담', detail: '분쟁 발생 즉시 전화·메시지 상담 가능' },
  { label: '의료과실 여부 판단', detail: '의사 출신 변호사의 임상 기준 사전 감정' },
  { label: '협상 대행', detail: '환자 및 보건기관과의 공식 협상 전 과정 대행' },
  { label: '내용증명·경고장 발송', detail: '법무법인 오킴스 명의의 공식 법률 문서 발송' },
  { label: '보건소·행정기관 소통 대행', detail: '행정처분 최소화를 위한 공식 대응 전략 포함' },
  { label: '합의금 산정', detail: '적정 합의 범위를 법적 기준에 따라 산정' },
];

const STEPS = [
  {
    n: '01',
    title: '온라인 상담 신청',
    desc: '홈페이지 문의 폼을 통해 상황을 간략히 기재합니다. 5분이면 충분합니다.',
  },
  {
    n: '02',
    title: '전담 변호사 배정',
    desc: '신청 후 24시간 이내, 의사 출신 변호사를 포함한 담당 팀이 배정됩니다.',
  },
  {
    n: '03',
    title: '첫 상담 및 전략 수립',
    desc: '사건 내용을 파악하고 대응 방향과 예상 비용을 사전에 안내해드립니다.',
  },
  {
    n: '04',
    title: '즉시 서비스 시작',
    desc: '견적 확인 후 계약을 체결하면 즉각 법률 대응이 개시됩니다.',
  },
];

const HERO_IMG =
  'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=900&auto=format&fit=crop&q=80';
const SECTION_IMG =
  'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&auto=format&fit=crop&q=80';

export function ContractPage() {
  const { ref: heroRef, inView: heroIn } = useInView({ threshold: 0.1 });
  const { ref: featHeadRef, inView: featHeadIn } = useInView();
  const { ref: featGridRef, inView: featGridIn } = useInView();
  const { ref: photoRef, inView: photoIn } = useInView({ threshold: 0.2 });
  const { ref: includesRef, inView: includesIn } = useInView();
  const { ref: tableRef, inView: tableIn } = useInView();
  const { ref: processHeadRef, inView: processHeadIn } = useInView();
  const { ref: stepsRef, inView: stepsIn } = useInView();

  return (
    <div className={styles.page}>
      <Seo
        title="단건 계약 안내 — medilawyer"
        description="분쟁이 발생한 그 순간, 필요한 서비스만 즉시 이용하세요. 월정액 없이 사건별로 긴급 법률 상담·의료과실 판단·협상 대행·내용증명 등 전문 법률 지원을 받을 수 있습니다."
        path="/contract"
        jsonLd={breadcrumbJsonLd([
          { name: '홈', path: '/' },
          { name: '단건 계약 안내', path: '/contract' },
        ])}
      />
      {/* ── Hero ── */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div ref={heroRef} className={`${styles.heroLeft} reveal ${heroIn ? 'visible' : ''}`}>
            <Link to="/" className={styles.back}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13 8H3M7 12l-4-4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              홈으로
            </Link>
            <h1 className={styles.heroTitle}>
              단건 계약
              <br />
              안내
            </h1>
            <p className={styles.heroDesc}>
              분쟁이 발생한 그 순간, 필요한 서비스만 즉시 이용하세요. 월정액 없이 사건별로 전문 법률
              지원을 받을 수 있습니다.
            </p>
            <a href="#page-inquiry" className={styles.heroCta}>
              지금 상담 신청하기
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 8h10M9 4l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>
          <div className={styles.heroRight}>
            <div className={styles.heroImgWrap}>
              <img src={HERO_IMG} alt="법률 상담 미팅" className={styles.heroImg} />
              <div className={styles.heroImgOverlay} />
              <div className={styles.heroImgBadge}>
                <div className={styles.badgeVal}>24h</div>
                <div className={styles.badgeLabel}>이내 변호사 배정</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4 Features ── */}
      <section className={styles.features}>
        <div className="wrap">
          <div
            ref={featHeadRef}
            className={`${styles.featHead} reveal ${featHeadIn ? 'visible' : ''}`}
          >
            <h2 className={styles.secTitle}>단건 계약의 4가지 원칙</h2>
          </div>
          <div
            ref={featGridRef}
            className={`${styles.featGrid} reveal ${featGridIn ? 'visible' : ''}`}
          >
            {FEATURES.map((f) => (
              <div key={f.n} className={styles.featItem}>
                <span className={styles.featN}>{f.n}</span>
                <h3 className={styles.featTitle}>{f.title}</h3>
                <p className={styles.featDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Full-bleed editorial photo ── */}
      <div ref={photoRef} className={`${styles.photoBreak} reveal ${photoIn ? 'visible' : ''}`}>
        <img src={SECTION_IMG} alt="" aria-hidden="true" className={styles.photoBreakImg} />
        <div className={styles.photoBreakOverlay} />
        <div className={styles.photoBreakContent}>
          <p className={styles.photoBreakQuote}>
            법무법인 오킴스 의료팀은
            <br />
            의사이자 변호사입니다
          </p>
          <span className={styles.photoBreakCredit}>의사·치과의사 출신 변호사 직속 팀</span>
        </div>
      </div>

      {/* ── What's included ── */}
      <section className={styles.includes}>
        <div className="wrap">
          <div
            ref={includesRef}
            className={`${styles.includesInner} reveal ${includesIn ? 'visible' : ''}`}
          >
            <div className={styles.includesLeft}>
              <h2 className={styles.secTitle}>단건 계약에 포함되는 서비스</h2>
              <p className={styles.includesNote}>
                단건 계약은 아래 서비스 중 해당 사건에 필요한 항목이 모두 포함됩니다. 추가 비용 없이
                한 사건을 처음부터 끝까지 담당합니다.
              </p>
            </div>
            <ul className={styles.includesList}>
              {INCLUDES.map((item, i) => (
                <li key={i} className={styles.includesItem}>
                  <div className={styles.includesMark} />
                  <div>
                    <div className={styles.includesLabel}>{item.label}</div>
                    <div className={styles.includesDetail}>{item.detail}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Service detail table ── */}
          <div
            ref={tableRef}
            className={`${styles.serviceTable} reveal ${tableIn ? 'visible' : ''}`}
          >
            <p className={styles.serviceTableLabel}>서비스 상세 내역</p>
            <table className={styles.stTable}>
              <thead>
                <tr>
                  <th className={styles.stTh}>서비스</th>
                  <th className={styles.stTh}>상세 내용</th>
                  <th className={`${styles.stTh} ${styles.stThFreq}`}>제공 횟수</th>
                </tr>
              </thead>
              <tbody>
                {serviceTableData.map((row) => (
                  <tr key={row.service} className={styles.stTr}>
                    <td className={`${styles.stTd} ${styles.stTdService}`}>
                      {row.service.replace('\n', ' ')}
                    </td>
                    <td className={styles.stTd}>
                      <ul className={styles.stDetailList}>
                        {row.details.map((d) => (
                          <li key={d}>{d}</li>
                        ))}
                      </ul>
                    </td>
                    <td className={`${styles.stTd} ${styles.stTdFreq}`}>
                      <span className={styles.stFreq}>{row.frequency}</span>
                      {row.note && <span className={styles.stFreqNote}>{row.note}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className={styles.serviceTableFootnote}>
              * 월정액 정기자문 계약의 경우 제공 범위 및 횟수는 별도 협의로 결정됩니다.
            </p>
          </div>
        </div>
      </section>

      {/* ── Process ── */}
      <section className={styles.process}>
        <div className="wrap">
          <div
            ref={processHeadRef}
            className={`${styles.secHead} reveal ${processHeadIn ? 'visible' : ''}`}
          >
            <h2 className={styles.secTitleLight}>진행 프로세스</h2>
          </div>
          <div ref={stepsRef} className={`${styles.stepList} reveal ${stepsIn ? 'visible' : ''}`}>
            {STEPS.map((s, i) => (
              <div key={s.n} className={styles.stepRow}>
                <span className={styles.stepN}>{s.n}</span>
                <div className={styles.stepBody}>
                  <h3 className={styles.stepTitle}>{s.title}</h3>
                  <p className={styles.stepDesc}>{s.desc}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={styles.stepConnector}>
                    <svg width="16" height="24" viewBox="0 0 16 24" fill="none">
                      <path
                        d="M8 0v20M2 14l6 6 6-6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Inquiry (공용 CTA + 모달 플로우) ── */}
      <Inquiry inline serviceContext="contract" />
    </div>
  );
}
